/**
 * Цепочка этапов проекта и сроки этапов — один контракт для сервера и клиента (клиент: алиас `@stages`).
 * Любой список этапов, подстатусов или полей сроков берётся отсюда, копий нет.
 */

/** Этапы проекта по порядку производства */
export const PROJECT_STAGES = [
  'Добавлен',
  'Запись',
  'Монтаж',
  'Проверка',
  'Правки',
  'Проверка правок',
  'Готов',
] as const;

export type ProjectStage = (typeof PROJECT_STAGES)[number];

/** Подстатусы этапов; состояние хранится как «Этап» или «Этап: Подстатус» */
export const STAGE_SUBSTATUSES: Record<ProjectStage, readonly string[]> = {
  Добавлен: [],
  Запись: ['Завершена'],
  Монтаж: ['В работе', 'Завершён'],
  Проверка: ['В работе', 'Завершена'],
  Правки: ['Подготовлены', 'Записаны', 'Внесены'],
  'Проверка правок': ['Завершена'],
  Готов: ['Частично оплачен', 'Полностью оплачен'],
};

/** Все допустимые значения state: базовые этапы, затем «Этап: Подстатус» по порядку этапов */
export const ALL_PROJECT_STATES: readonly string[] = [
  ...PROJECT_STAGES,
  ...PROJECT_STAGES.flatMap((stage) => STAGE_SUBSTATUSES[stage].map((sub) => `${stage}: ${sub}`)),
];

/**
 * Все состояния в порядке производства (воронка): этап, затем его подстатусы. n-е состояние записывает n-е событие воронки
 * (сервер: STATE_TO_EVENT / EVENT_ORDER, клиент: STATUS_EVENT_FUNNEL_ORDER) — соответствие закреплено тестом сервера.
 */
export const STATE_FUNNEL: readonly string[] = PROJECT_STAGES.flatMap((stage) => [
  stage,
  ...STAGE_SUBSTATUSES[stage].map((sub) => `${stage}: ${sub}`),
]);

const PROJECT_STATE_SET: ReadonlySet<string> = new Set(ALL_PROJECT_STATES);

export function isProjectState(state: string): boolean {
  return PROJECT_STATE_SET.has(state);
}

const READY_STAGE: ProjectStage = 'Готов';

/** Этап пустого или незнакомого состояния */
export const FALLBACK_STAGE: ProjectStage = 'Добавлен';

const PROJECT_STAGE_SET: ReadonlySet<string> = new Set(PROJECT_STAGES);

/** Базовый этап состояния «Этап» / «Этап: Подстатус»; пустое и незнакомое — «Добавлен» (так же ранжирует и сортирует сервер) */
export function getStateStage(state: string | null | undefined): ProjectStage {
  const base = state ? state.split(': ', 1)[0]! : '';
  return PROJECT_STAGE_SET.has(base) ? (base as ProjectStage) : FALLBACK_STAGE;
}

/** «Готов» или «Готов: …» — проект сдан и не входит в активные выборки */
export function isReadyState(state: string | null | undefined): boolean {
  if (state == null || state === '') return false;
  return state === READY_STAGE || state.startsWith(`${READY_STAGE}: `);
}

/** Поля `projects` со сроками этапов */
export type DeadlineField =
  | 'recording_start_deadline'
  | 'recording_end_deadline'
  | 'editing_deadline'
  | 'review_deadline'
  | 'corrections_deadline'
  | 'corrections_review_deadline'
  | 'deadline';

/** Срок этапа: поле проекта и подпись (подписи — дословно из спеки) */
export const STAGE_DEADLINE: Record<ProjectStage, { field: DeadlineField; label: string }> = {
  Добавлен: { field: 'recording_start_deadline', label: 'Начать запись до' },
  Запись: { field: 'recording_end_deadline', label: 'Завершить запись до' },
  Монтаж: { field: 'editing_deadline', label: 'Завершить монтаж до' },
  Проверка: { field: 'review_deadline', label: 'Завершить проверку до' },
  Правки: { field: 'corrections_deadline', label: 'Внести правки до' },
  'Проверка правок': { field: 'corrections_review_deadline', label: 'Завершить проверку правок до' },
  Готов: { field: 'deadline', label: 'Завершить производство до' },
};

/** Поля сроков в порядке этапов */
export const DEADLINE_FIELDS: readonly DeadlineField[] = PROJECT_STAGES.map((stage) => STAGE_DEADLINE[stage].field);

/** Подпись срока по полю проекта */
export const DEADLINE_FIELD_LABEL: Record<DeadlineField, string> = Object.fromEntries(
  PROJECT_STAGES.map((stage) => [STAGE_DEADLINE[stage].field, STAGE_DEADLINE[stage].label])
) as Record<DeadlineField, string>;

const DEADLINE_FIELD_SET: ReadonlySet<unknown> = new Set(DEADLINE_FIELDS);

export function isDeadlineField(value: unknown): value is DeadlineField {
  return DEADLINE_FIELD_SET.has(value);
}

/** Срок, по которому горит проект (список, сортировка «По срочности») */
export interface EffectiveDeadline {
  /** Текущий этап проекта */
  stage: ProjectStage;
  /** Поле срока: срок этапа или общий `deadline` */
  field: DeadlineField;
  /** «Завершить запись до» */
  label: string;
  /** Дата «ГГГГ-ММ-ДД» */
  value: string;
}

/**
 * Срок текущего этапа, если он задан, иначе общий «Завершить производство до» (у «Готов» срок этапа и есть общий).
 * Сервер сортирует по тому же правилу (projectSearchSortSql, SQL_EFFECTIVE_DEADLINE).
 */
export function getEffectiveDeadline(
  project: { state?: string | null } & { [K in DeadlineField]?: string | null }
): EffectiveDeadline | null {
  const stage = getStateStage(project.state);
  const own = STAGE_DEADLINE[stage];
  const ownValue = project[own.field];
  if (ownValue) return { stage, field: own.field, label: own.label, value: ownValue };
  const value = project.deadline;
  return value ? { stage, field: 'deadline', label: DEADLINE_FIELD_LABEL.deadline, value } : null;
}
